import { mapDepartment } from '@webapp/core/data-access/mappers/mapDepartment'
import { mapLocation } from '@webapp/core/data-access/mappers/mapLocation'
import { mapPaymentTermApiModel } from './mapPaymentTermApiModel'
import { mapShippingMethodApiModel } from './mapShippingMethodApiModel'
import { mapShippingTermApiModel } from './mapShippingTermApiModel'
import { mapVendorDocumentApiModel } from './mapVendorDocumentApiModel'
import { type VendorRequestConstructor } from '../../entities/VendorRequest'
import { type VendorRequestApiModel } from '../api-models/VendorRequestApiModel'

const mapVendorRequestUserToRequester = (
  user: VendorRequestApiModel['requester']
) => ({
  id: user.id,
  firstName: user.firstName,
  lastName: user.lastName,
  profileImage: user.profile_image || '',
  primaryCategory: user.department?.branch
    ? mapLocation(user.department.branch)
    : null,
  secondaryCategory: user.department ? mapDepartment(user.department) : null,
})

const mapVendorRequestUserToApprover = (
  user: VendorRequestApiModel['next_approver']
) => ({
  id: user.id,
  firstName: user.firstName,
  lastName: user.lastName,
  profileImage: user.profile_image || '',
  approvalDelegatee: null,
  primaryCategory: user.department?.branch
    ? mapLocation(user.department.branch)
    : null,
  secondaryCategory: user.department ? mapDepartment(user.department) : null,
  triggersFinalApproval: null,
})

export const mapVendorRequestApiModel = (
  data: VendorRequestApiModel
): VendorRequestConstructor => ({
  id: data.id,
  reason: data.reason,
  createdAt: data.created_at,
  status: data.status,
  requester: mapVendorRequestUserToRequester(data.requester),
  nextApprover: mapVendorRequestUserToApprover(data.next_approver),
  nextApproverChoices: data.next_approver_choices.map((approver) =>
    mapVendorRequestUserToApprover(approver)
  ),
  vendorId: data.vendor_id,
  name: data.name,
  type: data.type,
  address: {
    addressLineOne: data.address_line_one,
    addressLineTwo: data.address_line_two,
    postalCode: data.postal_code,
    city: data.city,
    stateProvince: data.state_province,
    country: data.country,
  },
  contactInfo: {
    phoneOne: data.phone_one,
    phoneTwo: data.phone_two,
    fax: data.fax,
    email: data.email,
    contact: data.contact,
    url: data.url,
  },
  externalId: data.external_id,
  paymentTerm: data.payment_term_ref
    ? mapPaymentTermApiModel(data.payment_term_ref)
    : null,
  shippingTerm: data.shipping_term_ref
    ? mapShippingTermApiModel(data.shipping_term_ref)
    : null,
  shippingMethod: data.shipping_method_ref
    ? mapShippingMethodApiModel(data.shipping_method_ref)
    : null,
  pdfLabelSet: data.po_pdf_labels,
  isAutoEmailPoEnabled: data.is_auto_email_po_enabled,
  attachments: data.attachments.map(mapVendorDocumentApiModel),
  permissions: {
    canEdit: data.permissions.can_edit,
    canApprove: data.permissions.can_approve,
    canDelete: data.permissions.can_delete,
  },
})
